import { backendServices } from 'common';
import {
    BuildSimDocumentArgs,
    SIM_DOCUMENT_CLASS,
    SimSubmissionContext,
    buildSimDocumentViewModel,
} from '../../_shared/simDocumentVM';
import { RemittanceFormViewModel } from './RemittanceFormViewModel';
import { z } from 'zod';

/**
 * Build the HeaderInfo payload for the Remittance SIM document.
 *
 * Existing skeleton paths (mapped directly):
 *   TIN, SWIFT, IBAN, ABARoutingNumber, Instructions,
 *   Address1/City/State/PostalCode/Country, Phone, Email
 *
 * Additional fields written under HeaderInfo:
 *   BankName, BankStreetAddress, BankCity, BankState, BankPostalCode,
 *   BankCountry, AccountNumber, PreferredPaymentMethod, PaymentTerms,
 *   DefaultPaymentCurrency, SortCode
 */
function remittanceFormToHeaderInfo(
    form: RemittanceFormViewModel,
    tin: string | undefined
): Record<string, string | undefined> {
    return {
        TIN: tin,
        SWIFT: form.SWIFT,
        IBAN: form.IBAN,
        ABARoutingNumber: form.ABARoutingNumber,
        Instructions: form.Instructions,

        Address1: form.RemittanceAddress1,
        City: form.RemittanceCity,
        State: form.RemittanceState,
        PostalCode: form.RemittancePostalCode,
        Country: form.RemittanceCountry,
        Phone: form.RemittancePhone,
        Email: form.RemittanceEmail,

        BankName: form.BankName,
        BankStreetAddress: form.BankAddress1,
        BankCity: form.BankCity,
        BankState: form.BankState,
        BankPostalCode: form.BankPostalCode,
        BankCountry: form.BankCountry,
        AccountNumber: form.AccountNumber,

        PreferredPaymentMethod: form.PreferredPaymentMethod,
        PaymentTerms: form.PaymentTerms,
        DefaultPaymentCurrency: form.DefaultPaymentCurrency,
        SortCode: form.SortCode,
    };
}

export function convertRemittanceFormToVM(
    form: RemittanceFormViewModel,
    tin: string | undefined,
    skeleton: BuildSimDocumentArgs['skeleton'],
    context: SimSubmissionContext,
    simDocumentTypeId: number | null,
    scheduleDescription?: string | null,
    simRequestId?: number
): backendServices.ViewModels.DocumentViewModel {
    return buildSimDocumentViewModel({
        skeleton,
        context,
        documentClass: SIM_DOCUMENT_CLASS.Remittance,
        description: scheduleDescription || 'Remittance Information',
        documentType: 'Remittance',
        headerInfo: remittanceFormToHeaderInfo(form, tin),
        simDocumentTypeId,
        simRequestId,
    });
}

const dropdownOptionSchema = z.object({
    Label: z.string(),
    Value: z.string(),
});

const dataMappingSchema = z
    .object({
        PaymentMethodMapping: z
            .object({
                DefaultPaymentMethodValue: z
                    .string()
                    .trim()
                    .optional()
                    .transform((val) => val || undefined),
                PaymentMethods: z.object({
                    PaymentMethod: z
                        .array(dropdownOptionSchema)
                        .or(dropdownOptionSchema)
                        .transform((val) => (Array.isArray(val) ? val : [val])),
                }),
            })
            .passthrough(),
        PaymentTermMapping: z
            .object({
                DefaultPaymentTermValue: z
                    .string()
                    .trim()
                    .optional()
                    .transform((val) => val || undefined),
                PaymentTerms: z.object({
                    PaymentTerm: z
                        .array(dropdownOptionSchema)
                        .or(dropdownOptionSchema)
                        .transform((val) => (Array.isArray(val) ? val : [val])),
                }),
            })
            .passthrough(),
    })
    .passthrough();

export type PaymentFieldConfiguration = {
    PaymentTerms: {
        DefaultValue?: string;
        Options: {
            Value: string;
            Label: string;
        }[];
    };
    PaymentMethods: {
        DefaultValue?: string;
        Options: {
            Value: string;
            Label: string;
        }[];
    };
};

export function extractPaymentFieldConfiguration(
    vendorCustomAttributes: backendServices.ViewModels.CompanyViewModel['VendorCustomAttributes']
): PaymentFieldConfiguration | null {
    const dataMapping = vendorCustomAttributes?.root?.DataMapping ?? null;

    if (typeof dataMapping !== 'object' || dataMapping === null) {
        return null;
    }

    if (Object.keys(dataMapping).length === 0) {
        return null;
    }

    const parsedDataMapping = dataMappingSchema.safeParse(dataMapping);

    if (!parsedDataMapping.success) {
        console.error('Failed to parse data mapping', parsedDataMapping.error);
        return null;
    }

    const { PaymentMethodMapping, PaymentTermMapping } = parsedDataMapping.data;

    return {
        PaymentTerms: {
            DefaultValue: PaymentTermMapping.DefaultPaymentTermValue,
            Options: PaymentTermMapping.PaymentTerms.PaymentTerm,
        },
        PaymentMethods: {
            DefaultValue: PaymentMethodMapping.DefaultPaymentMethodValue,
            Options: PaymentMethodMapping.PaymentMethods.PaymentMethod,
        },
    };
}
